import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	ColorPicker,
	IconPicker,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			category,
			postCount,
			blockPadding,
			blockMargin,
			enableIcon,
			icon,
			iconGap,
			iconSize,
			iconColor,
			enableLabel,
			label,
			enableArrows,
			color,
			colorHover,
			textColorHover,
			textColor,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			labelTypography,
			bodyTypography,
			tickerEffect,
			tickerDirection,
			delayTimer,
			autoPlay,
			scrollSpeed,
			stopOnHover,
		},
		setAttributes,
		categories,
	} = props;

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Post Options", "magazine-blocks")}>
					<Select
						value={category}
						onChange={(val) => setAttributes({ category: val })}
						label={__("Category", "magazine-blocks")}
						options={[
							{
								value: "all",
								label: __("All", "magazine-blocks"),
							},
							...(categories ?? [])?.map(({ name, id }) => ({
								label: name,
								value: id.toString(),
							})),
						]}
					/>
					<Slider
						label={__("Post Count", "magazine-blocks")}
						min={1}
						max={20}
						value={postCount}
						onChange={(val: any) =>
							setAttributes({ postCount: val })
						}
						resetAttributeKey="postCount"
					/>
				</Panel>
				<Panel title={__("Ticker Label", "magazine-blocks")}>
					<Toggle
						checked={enableLabel}
						onChange={() =>
							setAttributes({
								enableLabel: !enableLabel,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableLabel && (
						<Input
							onChange={(val) => setAttributes({ label: val })}
							// @ts-ignore
							labelPosition="top"
							label={__("Label", "magazine-blocks")}
							value={label || ""}
						/>
					)}
				</Panel>
				<Panel title={__("Arrows", "magazine-blocks")}>
					<Toggle
						label={__("Enable Arrows", "magazine-blocks")}
						checked={enableArrows || false}
						onChange={() =>
							setAttributes({ enableArrows: !enableArrows })
						}
					/>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<>
						<Toggle
							checked={enableIcon}
							onChange={() =>
								setAttributes({
									enableIcon: !enableIcon,
								})
							}
							label={__("Enable", "magazine-blocks")}
						/>
						{enableIcon && (
							<>
								<IconPicker
									value={icon}
									onChange={(val) =>
										setAttributes({ icon: val })
									}
								/>
								<Slider
									label={__("Size", "magazine-blocks")}
									min={0}
									max={50}
									value={iconSize}
									onChange={(val: any) =>
										setAttributes({ iconSize: val })
									}
									responsive
									units={["px", "em", "%"]}
									resetAttributeKey="iconSize"
								/>
								<Slider
									label={__("Gap", "magazine-blocks")}
									min={0}
									max={60}
									value={iconGap}
									onChange={(val: any) =>
										setAttributes({ iconGap: val })
									}
									responsive
									units={["px", "em", "%"]}
									resetAttributeKey="iconGap"
								/>
							</>
						)}
					</>
				</Panel>
				<Panel title={__("Animation", "magazine-blocks")}>
					{tickerEffect !== "none" && (
						<div
							style={{
								marginBottom: "16px",
								padding: "12px",
								backgroundColor: "#fff3cd",
								border: "1px solid #ffeaa7",
								borderRadius: "4px",
								fontSize: "13px",
								color: "#856404",
							}}
						>
							<strong>
								{__(
									"Transition Effect Info:",
									"magazine-blocks"
								)}
							</strong>
							<br />
							{__(
								"Animation Speed controls how fast transitions happen. Delay Timer controls pause between items.",
								"magazine-blocks"
							)}
						</div>
					)}
					{tickerEffect === "none" && (
						<div
							style={{
								marginBottom: "16px",
								padding: "12px",
								backgroundColor: "#d4edda",
								border: "1px solid #c3e6cb",
								borderRadius: "4px",
								fontSize: "13px",
								color: "#155724",
							}}
						>
							<strong>
								{__("No Animation Info:", "magazine-blocks")}
							</strong>
							<br />
							{__(
								"No animations will be applied. Content will change instantly.",
								"magazine-blocks"
							)}
						</div>
					)}
					<Select
						value={tickerEffect}
						onChange={(val) => setAttributes({ tickerEffect: val })}
						label={__("Ticker Effect", "magazine-blocks")}
						options={[
							{
								value: "fade",
								label: __("Fade", "magazine-blocks"),
							},
							{
								value: "slide-left",
								label: __("Slide Left", "magazine-blocks"),
							},
							{
								value: "slide-right",
								label: __("Slide Right", "magazine-blocks"),
							},
							{
								value: "none",
								label: __("None", "magazine-blocks"),
							},
						]}
					/>
					<Select
						value={tickerDirection}
						onChange={(val) =>
							setAttributes({ tickerDirection: val })
						}
						label={__("Direction", "magazine-blocks")}
						options={[
							{
								value: "ltr",
								label: __("Left to Right", "magazine-blocks"),
							},
							{
								value: "rtl",
								label: __("Right to Left", "magazine-blocks"),
							},
						]}
					/>
					<Slider
						label={__(
							"Delay Between Transitions (ms)",
							"magazine-blocks"
						)}
						min={0}
						max={5000}
						value={delayTimer}
						onChange={(val: any) =>
							setAttributes({ delayTimer: val })
						}
						resetAttributeKey="delayTimer"
					/>
					<Toggle
						label={__("Auto Play", "magazine-blocks")}
						checked={autoPlay || false}
						onChange={() => setAttributes({ autoPlay: !autoPlay })}
					/>
					<Slider
						label={__("Animation Speed (1-100)", "magazine-blocks")}
						min={1}
						max={100}
						value={scrollSpeed}
						onChange={(val: any) =>
							setAttributes({ scrollSpeed: val })
						}
						resetAttributeKey="scrollSpeed"
					/>
					<Toggle
						label={__("Stop On Hover", "magazine-blocks")}
						checked={stopOnHover || false}
						onChange={() =>
							setAttributes({ stopOnHover: !stopOnHover })
						}
					/>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Ticker Label", "magazine-blocks")}>
					<Typography
						value={labelTypography}
						onChange={(val) =>
							setAttributes({ labelTypography: val })
						}
						resetAttributeKey="labelTypography"
					/>
					<PopoverDrawer
						label={__("Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: color, id: "color" },
										{
											color: colorHover,
											id: "colorHover",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) => {
											setAttributes({ color: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={colorHover}
										resetKey="postTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												colorHover: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={colorHover}
										onChange={(v) =>
											setAttributes({ colorHover: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Ticker Body", "magazine-blocks")}>
					<Typography
						value={bodyTypography}
						onChange={(val) =>
							setAttributes({ bodyTypography: val })
						}
						resetAttributeKey="bodyTypography"
					/>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: textColor, id: "textColor" },
										{
											color: textColorHover,
											id: "textColorHover",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={textColor}
										resetKey="textColor"
										onReset={(v) => {
											setAttributes({ textColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={textColor}
										onChange={(v) =>
											setAttributes({ textColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={textColorHover}
										resetKey="postTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												textColorHover: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={textColorHover}
										onChange={(v) =>
											setAttributes({ textColorHover: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: iconColor,
											id: "iconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconColor}
										resetKey="iconColor"
										onReset={(v) =>
											setAttributes({ iconColor: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={iconColor}
										onChange={(v) =>
											setAttributes({ iconColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
